import axios, { type AxiosInstance } from 'axios';
import toast from 'react-hot-toast';
import { env } from '@/config/env';
import { ADMIN_USER_KEY } from '@/constants/storage';
import { ROUTES } from '@/constants/routes';

/**
 * Axios instance for the admin dashboard ONLY.
 * Sends the access token and the CSRF token. The kiosk flow keeps using
 * `@/api/client`, which sends neither.
 */
const adminApi: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 30000,
  withCredentials: true, // so the browser sends/receives the CSRF cookie
  headers: { 'Content-Type': 'application/json' },
});

const SAFE_METHODS = ['get', 'head', 'options'];

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  const value = match?.[1];
  return value ? decodeURIComponent(value) : null;
}

// ── Request: access token + CSRF token ────────────────────────────────────
adminApi.interceptors.request.use((config) => {
  if (!SAFE_METHODS.includes((config.method ?? 'get').toLowerCase())) {
    const csrf = getCookie('csrftoken');
    if (csrf) config.headers['X-CSRFToken'] = csrf;
  }
  return config;
});

// ── Response: network errors + expired session ────────────────────────────
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      toast.error(
        error.code === 'ECONNABORTED'
          ? 'The request timed out. Please try again.'
          : 'Unable to connect to the server. Please check your network and try again.',
        { id: 'network-error' },
      );
    } else if (error.response.status === 401 && window.location.pathname !== ROUTES.admin.login) {
      localStorage.removeItem(ADMIN_USER_KEY);
      toast.error('Session expired. Please log in again.', { id: 'session-expired' });
      window.location.assign(ROUTES.admin.login);
    }
    return Promise.reject(error);
  },
);

export default adminApi;